import { createFileRoute, redirect } from "@tanstack/react-router";
import React, { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Stethoscope,
  ClipboardList,
  Shield,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Storage } from "@/common/utils/storage";

const LOGO_PATH = "/static/images/logo.png";

export const Route = createFileRoute("/")({
  beforeLoad: async () => {
    const lastLogin = await Storage.getItem("lastLogin");
    if (lastLogin === "org") {
      throw redirect({
        to: "/org/home",
      });
    } else if (lastLogin === "provider") {
      throw redirect({
        to: "/provider/home",
      });
    }
  },

  component: HomePage,
});

function HomePage() {
  const navigate = useNavigate();
  const [showProviderDetails, setShowProviderDetails] = useState(false);
  const [showStaffingDetails, setShowStaffingDetails] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900">
      <div className="safe-area-top" />
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.03)_1px,transparent_0)] [background-size:40px_40px]"></div>

      <div className="relative z-10 flex items-center justify-center p-4 sm:p-6">
        <div className="max-w-6xl w-full">
          {/* Header Section */}
          <div className="text-center mb-6 sm:mb-12">
            <div className="p-4 sm:p-6 mb-4 sm:mb-2">
              <img
                src={LOGO_PATH}
                alt="Saile Company Logo"
                width={180}
                className="mx-auto brightness-0 invert"
              />
            </div>

            {/* Trust indicators */}
            <div className="flex items-center justify-center gap-4 sm:gap-8 text-blue-200 text-xs sm:text-sm flex-wrap">
              <div className="flex items-center gap-2">
                <Shield className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Secure Platform</span>
              </div>
              <div className="w-px h-4 bg-blue-300 hidden sm:block"></div>
              <div>24/7 Support</div>
              <div className="w-px h-4 bg-blue-300 hidden sm:block"></div>
              <div>Trusted by Facilities</div>
            </div>
          </div>

          {/* Role Selection */}

          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {/* Healthcare Professional Portal */}
            <Card className="group relative bg-white/90 backdrop-blur-sm border border-white/30 hover:border-white/50 transition-all duration-300 hover:shadow-2xl shadow-xl">
              <div className="p-6 sm:p-10">
                <div className="flex items-start gap-4 sm:gap-6 mb-6 sm:mb-8">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-50 rounded-xl flex items-center justify-center border border-blue-200 flex-shrink-0">
                    <Stethoscope className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-0 sm:mb-2">
                      Healthcare Professionals
                    </h2>
                    <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
                      MD, DO, PA, NP, CRNA, etc.
                    </p>
                  </div>
                </div>

                {/* Learn more section */}
                <div className="mb-6 sm:mb-8">
                  <button
                    onClick={() => setShowProviderDetails(!showProviderDetails)}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
                  >
                    Learn more
                    {showProviderDetails ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>

                  {showProviderDetails && (
                    <div className="mt-4 space-y-3 animate-in slide-in-from-top-2 duration-200">
                      <div className="flex items-center gap-3 text-gray-600 text-sm sm:text-base">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0"></div>
                        <span>
                          Perfect for physicians (MD/DO), nurse practitioners,
                          physician assistants, CRNAs, and more
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600 text-sm sm:text-base">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0"></div>
                        <span>
                          Upload and manage professional credentials and
                          certifications
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600 text-sm sm:text-base">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0"></div>
                        <span>
                          Connect with your organization and keep your
                          professional information up to date
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600 text-sm sm:text-base">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0"></div>
                        <span>
                          Streamlined scheduling and compliance tracking
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <Button
                  onClick={() => navigate({ to: "/provider/home" })}
                  className="w-full py-3 sm:py-4 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors duration-200 shadow-md hover:shadow-lg text-sm sm:text-base"
                >
                  I'm a Healthcare Professional
                </Button>
              </div>
            </Card>

            {/* Organization Dashboard */}
            <Card className="group relative bg-white/90 backdrop-blur-sm border border-white/30 hover:border-white/50 transition-all duration-300 hover:shadow-2xl shadow-xl">
              <div className="p-6 sm:p-10">
                <div className="flex items-start gap-4 sm:gap-6 mb-6 sm:mb-8">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-200 flex-shrink-0">
                    <ClipboardList className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-0 sm:mb-0">
                      Organizations
                    </h2>
                    <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
                      Manage your team and credentials.
                    </p>
                  </div>
                </div>

                {/* Learn more section */}
                <div className="mb-6 sm:mb-8">
                  <button
                    onClick={() => setShowStaffingDetails(!showStaffingDetails)}
                    className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 text-sm font-medium transition-colors"
                  >
                    Learn more
                    {showStaffingDetails ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>

                  {showStaffingDetails && (
                    <div className="mt-4 space-y-3 animate-in slide-in-from-top-2 duration-200">
                      <div className="flex items-center gap-3 text-gray-600 text-sm sm:text-base">
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0"></div>
                        <span>Post jobs and recruit healthcare providers</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600 text-sm sm:text-base">
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0"></div>
                        <span>
                          Verify professional credentials and compliance
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600 text-sm sm:text-base">
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0"></div>
                        <span>Analytics and workforce optimization</span>
                      </div>
                    </div>
                  )}
                </div>

                <Button
                  onClick={() => navigate({ to: "/org/home" })}
                  className="w-full py-3 sm:py-4 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-colors duration-200 shadow-md hover:shadow-lg text-sm sm:text-base"
                >
                  I'm an Organization Administrator
                </Button>
              </div>
            </Card>
          </div>

          {/* Bottom section */}
          <div className="text-center mt-12 sm:mt-16">
            <p className="text-blue-200 text-xs sm:text-sm px-4">
              Trusted by healthcare facilities nationwide
            </p>
          </div>
        </div>
      </div>
      <div className="safe-area-top" />
    </div>
  );
}

export default HomePage;
